import { useMemo } from "react"
import { Path, useController, useFormContext } from "react-hook-form"

import useIsDesktop from "@hooks/useIsDesktop"
import useUser from "@hooks/useUser"

import DrawerHeader from "@atoms/BottomDrawer/DrawerHeader/DrawerHeader"
import { BottomDrawer, Toggle } from "@atoms/index"

import { InputName } from "@molecules/Form/inputs/Input/Input.types"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import MenuItem from "@molecules/MenuItem/MenuItem"

import { Settings } from "@templates/index"

import EmailNotificationTooltip from "@pages/Settings/SettingsNotifications/components/EmailNotificationTooltip/EmailNotificationTooltip"
import { useNotificationMethodsQuery } from "@pages/Settings/SettingsNotifications/components/NotificationMethodsSection/NotificationMethodsSection.store"
import SMSNotificationTooltip from "@pages/Settings/SettingsNotifications/components/SMSNotificationTooltip/SMSNotificationTooltip"

import styles from "./NotificationProperty.module.scss"
import { NotificationPropertyProps } from "./NotificationProperty.types"

export default function NotificationProperty<T extends InputName>(props: NotificationPropertyProps<T>) {
    const { label, supportText, isLoading, emailCheckboxName, smsCheckboxName } = props

    const isDesktop = useIsDesktop()
    const { user } = useUser()
    const form = useFormContext<T>()

    const { data: defaultMethodsPreference } = useNotificationMethodsQuery()

    const phoneNotificationsDisabled = !user?.phone || !user?.phone_verified || !defaultMethodsPreference?.contact_sms
    const emailNotificationsDisabled =
        !user?.email || !user?.email_verified || !defaultMethodsPreference?.contact_email
    const emailIsNotVerified = !!(user && !user?.email_verified)
    const phoneIsNotSet = !!(user && !user?.phone)
    const phoneIsNotVerified = !!(user && !user?.phone_verified)

    const { field: emailField } = useController({ name: emailCheckboxName as Path<T>, control: form.control })
    const { field: smsField } = useController({ name: smsCheckboxName as Path<T>, control: form.control })
    const emailCheckedValue = emailField.value as boolean
    const smsCheckedValue = smsField.value as boolean

    const notificationValue = useMemo(() => {
        const emailStatus = emailCheckedValue && !emailNotificationsDisabled ? "Email" : ""
        const smsStatus = smsCheckedValue && !phoneNotificationsDisabled ? "Text" : ""

        if (emailStatus && smsStatus) {
            return "Email, Text"
        } else if (emailStatus || smsStatus) {
            return emailStatus || smsStatus
        } else {
            return "Off"
        }
    }, [emailCheckedValue, smsCheckedValue, emailNotificationsDisabled, phoneNotificationsDisabled])

    if (isDesktop) {
        return (
            <ListItem>
                <ListItem.AvatarAndContent
                    content={
                        <ListItemContent
                            leftLabel={<ListItem.Label label={label} />}
                            leftCaption={supportText ? <ListItem.Caption caption={supportText} truncate={1} /> : null}
                        />
                    }
                />
                <ListItem.Actions>
                    <Settings.NotificationConfigPage.Controls>
                        <EmailNotificationTooltip>
                            <Toggle<T>
                                isDisabled={isLoading || emailNotificationsDisabled}
                                checked={emailCheckedValue}
                                name={emailCheckboxName}
                                isControlled={!emailNotificationsDisabled}
                                label="Email"
                                showLabel={true}
                                alignment="right"
                            />
                        </EmailNotificationTooltip>
                        <SMSNotificationTooltip>
                            <Toggle<T>
                                isDisabled={isLoading || phoneNotificationsDisabled}
                                checked={smsCheckedValue}
                                name={smsCheckboxName}
                                isControlled={!phoneNotificationsDisabled}
                                label="Text"
                                showLabel={true}
                                alignment="right"
                            />
                        </SMSNotificationTooltip>
                    </Settings.NotificationConfigPage.Controls>
                </ListItem.Actions>
            </ListItem>
        )
    } else {
        const emailCaption = emailNotificationsDisabled
            ? emailIsNotVerified
                ? "Verification required."
                : "Email notifications disabled."
            : undefined
        const smsCaption = phoneNotificationsDisabled
            ? phoneIsNotSet
                ? "Phone number not set."
                : phoneIsNotVerified
                  ? "Verification required."
                  : "Text notifications disabled."
            : undefined

        return (
            <>
                <BottomDrawer.Root>
                    <BottomDrawer.Trigger asChild={true}>
                        <ListItem isClickable={true} onClick={() => {}}>
                            <ListItem.AvatarAndContent
                                content={
                                    <ListItemContent
                                        leftLabel={<ListItem.Label label={label} />}
                                        leftCaption={
                                            supportText ? (
                                                <ListItem.Caption caption={supportText} truncate={1} />
                                            ) : null
                                        }
                                        rightLabel={<ListItem.Label label={notificationValue} variant="subtle" />}
                                    />
                                }
                            />
                            <ListItem.Actions showChevron={true} />
                        </ListItem>
                    </BottomDrawer.Trigger>
                    <BottomDrawer.Content>
                        <DrawerHeader.Root>
                            <DrawerHeader.Title title={label} />
                        </DrawerHeader.Root>
                        <div className={styles.drawerContent}>
                            <EmailNotificationTooltip>
                                <MenuItem
                                    variant="toggle"
                                    title="Email"
                                    caption={emailCaption}
                                    toggle={{
                                        isChecked: emailNotificationsDisabled ? false : emailCheckedValue,
                                        onCheckedChange: emailField.onChange,
                                    }}
                                    isDisabled={isLoading || emailNotificationsDisabled}
                                />
                            </EmailNotificationTooltip>
                            <SMSNotificationTooltip>
                                <MenuItem
                                    variant="toggle"
                                    title="Text"
                                    caption={smsCaption}
                                    toggle={{
                                        isChecked: phoneNotificationsDisabled ? false : smsCheckedValue,
                                        onCheckedChange: smsField.onChange,
                                    }}
                                    isDisabled={isLoading || phoneNotificationsDisabled}
                                />
                            </SMSNotificationTooltip>
                        </div>
                    </BottomDrawer.Content>
                </BottomDrawer.Root>
            </>
        )
    }
}
